import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { supabase } from './lib/supabase';

// ============================================================
// PRÉ-LANÇAMENTO: Landing original comentada, PreLaunch ativa
// GO-LIVE: descomentar Landing, comentar PreLaunchLanding,
//          e trocar PreLaunchProtectedRoute por ProtectedRoute
// ============================================================

// import Landing from './pages/Landing'; // GO-LIVE: descomentar
import PreLaunchLanding from './pages/PreLaunchLanding';
import ListaEspera from './pages/ListaEspera';

import Login from './pages/Login';
import Register from './pages/Register';
import Plans from './pages/Plans';
import Upload from './pages/Upload';
import Specifications from './pages/Specifications';
import Results from './pages/Results';
import AdminLogs from './pages/AdminLogs';
import AdminDashboard from './pages/AdminDashboard';
import AdminFeedbacks from './pages/AdminFeedbacks';
import AdminUsuarios from './pages/AdminUsuarios';
import AdminCub from './pages/AdminCub';
import OrcamentoCub from './pages/OrcamentoCub';
import OrcamentoResultadoCub from './pages/OrcamentoResultadoCub';
import OrcamentoSinapi from './pages/OrcamentoSinapi';
import OrcamentoUpload from './pages/OrcamentoUpload';
import OrcamentoAnalise from './pages/OrcamentoAnalise';
import OrcamentoValidacao from './pages/OrcamentoValidacao';
import OrcamentoResultado from './pages/OrcamentoResultado';
import AdminSinapi from './pages/AdminSinapi';
import AdminRoute from './components/AdminRoute';

// Blog pages
import BlogList from './pages/BlogList';
import BlogArticle from './pages/BlogArticle';
import BlogCategory from './pages/BlogCategory';
import AdminBlog from './pages/AdminBlog';
import AdminBlogEditor from './pages/AdminBlogEditor';
import AdminBlogCategorias from './pages/AdminBlogCategorias';

// New budget flow pages
import OrcamentoNovo from './pages/OrcamentoNovo';
import OrcamentoPlanta from './pages/OrcamentoPlanta';
import OrcamentoConfirmar from './pages/OrcamentoConfirmar';
import OrcamentoAnaliseNew from './pages/OrcamentoAnaliseNew';
import OrcamentoValidacaoNew from './pages/OrcamentoValidacaoNew';
import OrcamentoResultadoNew from './pages/OrcamentoResultadoNew';
import QcoPlan from './pages/QcoPlan';

// Auth pages
import ConfirmarEmail from './pages/ConfirmarEmail';
import AuthCallback from './pages/AuthCallback';
import EscolherPlano from './pages/EscolherPlano';

// Payment pages
import Checkout from './pages/Checkout';
import PagamentoSucesso from './pages/PagamentoSucesso';
import PagamentoErro from './pages/PagamentoErro';
import PagamentoPendente from './pages/PagamentoPendente';

// Legal pages
import TermosDeUso from './pages/TermosDeUso';
import PoliticaReembolso from './pages/PoliticaReembolso';

// My budgets
import MeusOrcamentos from './pages/MeusOrcamentos';
import Pitch from './pages/Pitch';

// ── Rota protegida padrão (sem login → /login) ──
// eslint-disable-next-line no-unused-vars
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ── Rota protegida PRÉ-LANÇAMENTO (sem login → /login, não-admin → /lista-espera) ──
// GO-LIVE: substituir todas as PreLaunchProtectedRoute por ProtectedRoute
function PreLaunchProtectedRoute({ children }) {
  const { user } = useAuth();
  const [plano, setPlano] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlano = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await supabase
          .from('profiles')
          .select('plano')
          .eq('user_id', user.id)
          .single();
        setPlano(data?.plano || 'gratis');
      } catch {
        setPlano('gratis');
      } finally {
        setLoading(false);
      }
    };
    fetchPlano();
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;
  if (loading) return null; // Evita flash enquanto carrega plano
  if (plano !== 'admin') return <Navigate to="/lista-espera" replace />;
  return children;
}

function App() {
  return (
    <div className="min-h-screen bg-white">
      <Routes>
        {/* ── Pré-lançamento ── */}
        <Route path="/" element={<PreLaunchLanding />} />
        {/* GO-LIVE: descomentar a linha abaixo e comentar a de cima */}
        {/* <Route path="/" element={<Landing />} /> */}

        <Route path="/lista-espera" element={<ListaEspera />} />

        {/* ── Rotas públicas ── */}
        <Route path="/login" element={<Login />} />
        <Route path="/registro" element={<Register />} />
        <Route path="/confirmar-email" element={<ConfirmarEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/termos-de-uso" element={<TermosDeUso />} />
        <Route path="/politica-de-reembolso" element={<PoliticaReembolso />} />
        <Route path="/pitch" element={<Pitch />} />

        {/* Blog - public routes */}
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/categoria/:slug" element={<BlogCategory />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />

        {/* ── Rotas protegidas (PRÉ-LANÇAMENTO: só admin acessa) ── */}
        {/* GO-LIVE: trocar PreLaunchProtectedRoute por ProtectedRoute em todas */}

        <Route path="/escolher-plano" element={<PreLaunchProtectedRoute><EscolherPlano /></PreLaunchProtectedRoute>} />

        {/* Payment routes */}
        <Route path="/checkout" element={<PreLaunchProtectedRoute><Checkout /></PreLaunchProtectedRoute>} />
        <Route path="/checkout/:plano" element={<PreLaunchProtectedRoute><Checkout /></PreLaunchProtectedRoute>} />
        <Route path="/pagamento/sucesso" element={<PreLaunchProtectedRoute><PagamentoSucesso /></PreLaunchProtectedRoute>} />
        <Route path="/pagamento/erro" element={<PagamentoErro />} />
        <Route path="/pagamento/pendente" element={<PagamentoPendente />} />

        {/* Protected routes */}
        <Route path="/meus-orcamentos" element={<PreLaunchProtectedRoute><MeusOrcamentos /></PreLaunchProtectedRoute>} />
        <Route path="/planos" element={<PreLaunchProtectedRoute><Plans /></PreLaunchProtectedRoute>} />
        <Route path="/upload" element={<PreLaunchProtectedRoute><Upload /></PreLaunchProtectedRoute>} />
        <Route path="/especificacoes" element={<PreLaunchProtectedRoute><Specifications /></PreLaunchProtectedRoute>} />
        <Route path="/resultados" element={<PreLaunchProtectedRoute><Results /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento-cub" element={<PreLaunchProtectedRoute><OrcamentoCub /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/resultado-cub" element={<PreLaunchProtectedRoute><OrcamentoResultadoCub /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento-sinapi" element={<PreLaunchProtectedRoute><OrcamentoSinapi /></PreLaunchProtectedRoute>} />

        {/* Original budget flow */}
        <Route path="/orcamento" element={<PreLaunchProtectedRoute><OrcamentoUpload /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/analise" element={<PreLaunchProtectedRoute><OrcamentoAnalise /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/validacao" element={<PreLaunchProtectedRoute><OrcamentoValidacao /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/resultado" element={<PreLaunchProtectedRoute><OrcamentoResultado /></PreLaunchProtectedRoute>} />

        {/* New budget flow */}
        <Route path="/orcamento/novo" element={<PreLaunchProtectedRoute><OrcamentoNovo /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/planta" element={<PreLaunchProtectedRoute><OrcamentoPlanta /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/confirmar" element={<PreLaunchProtectedRoute><OrcamentoConfirmar /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/:id/analise" element={<PreLaunchProtectedRoute><OrcamentoAnaliseNew /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/:id/validacao" element={<PreLaunchProtectedRoute><OrcamentoValidacaoNew /></PreLaunchProtectedRoute>} />
        <Route path="/orcamento/:id/resultado" element={<PreLaunchProtectedRoute><OrcamentoResultadoNew /></PreLaunchProtectedRoute>} />
        <Route path="/qco-plan" element={<PreLaunchProtectedRoute><QcoPlan /></PreLaunchProtectedRoute>} />

        {/* ── Admin routes (não muda — AdminRoute já verifica admin) ── */}
        <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/logs" element={<AdminRoute><AdminLogs /></AdminRoute>} />
        <Route path="/admin/feedbacks" element={<AdminRoute><AdminFeedbacks /></AdminRoute>} />
        <Route path="/admin/usuarios" element={<AdminRoute><AdminUsuarios /></AdminRoute>} />
        <Route path="/admin/cub" element={<AdminRoute><AdminCub /></AdminRoute>} />
        <Route path="/admin/sinapi" element={<AdminRoute><AdminSinapi /></AdminRoute>} />

        {/* Admin blog routes */}
        <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
        <Route path="/admin/blog/novo" element={<AdminRoute><AdminBlogEditor /></AdminRoute>} />
        <Route path="/admin/blog/:id/editar" element={<AdminRoute><AdminBlogEditor /></AdminRoute>} />
        <Route path="/admin/blog/categorias" element={<AdminRoute><AdminBlogCategorias /></AdminRoute>} />
      </Routes>
    </div>
  );
}

export default App;
